import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MewsCredentialsForm } from "./form/MewsCredentialsForm";
import { OperaCredentialsForm } from "./form/OperaCredentialsForm";
import { OperaCredentialsFormData } from "../utils/pmsSchemas";
import {
  CredentialsStatus,
  PmsSettings,
  UpdatePmsCredentialsRequest,
} from "@/types/pms";
import { PmsType } from "@/types/hotel";
import {
  PMS_PROVIDER_LABEL_KEYS,
  resolveActivePmsProvider,
} from "@/utils/pmsProviderLabels";
import { Loader2, Plug, Trash2 } from "lucide-react";

interface CredentialsFormProps {
  hasCredentials: boolean;
  settings: PmsSettings | null;
  credentials: CredentialsStatus | null;
  canWrite: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isTesting: boolean;
  onSave: (data: UpdatePmsCredentialsRequest) => void;
  onDelete: () => void;
  onTest: () => void;
  hotelProvider?: PmsType;
  operaMissingFields?: (keyof OperaCredentialsFormData)[];
}

export const CredentialsForm = ({
  hasCredentials,
  settings,
  credentials,
  canWrite,
  isSaving,
  isDeleting,
  isTesting,
  onSave,
  onDelete,
  onTest,
  hotelProvider,
  operaMissingFields,
}: CredentialsFormProps) => {
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const storedProvider = resolveActivePmsProvider(
    settings?.provider,
    credentials?.provider,
    hotelProvider,
  );
  const initialProvider = storedProvider ?? PmsType.MEWS;

  const [provider, setProvider] = React.useState<PmsType>(initialProvider);

  React.useEffect(() => {
    setProvider(initialProvider);
  }, [initialProvider]);

  const showSwitchWarning =
    hasCredentials && !!storedProvider && provider !== storedProvider;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t("pms.credentials.title")}</CardTitle>
            <CardDescription>
              {t("pms.credentials.description")}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {hasCredentials && (
              <Button
                variant="new_outline"
                size="sm"
                disabled={!canWrite || isTesting}
                onClick={onTest}
              >
                {isTesting ? <Loader2 className="animate-spin" /> : <Plug />}
                {isTesting
                  ? t("pms.connection.testing")
                  : t("pms.connection.test")}
              </Button>
            )}
            {hasCredentials && (
              <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="new_destructive"
                    size="sm"
                    disabled={!canWrite}
                  >
                    <Trash2 />
                    {t("pms.credentials.delete")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t("pms.credentials.deleteConfirmTitle")}
                    </DialogTitle>
                    <DialogDescription>
                      {t("pms.credentials.deleteConfirmDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="new_outline"
                      onClick={() => setDeleteOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="new_destructive"
                      disabled={!canWrite || isDeleting}
                      onClick={() => {
                        onDelete();
                        setDeleteOpen(false);
                      }}
                    >
                      {t("pms.credentials.delete")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="provider">{t("pms.credentials.provider")}</Label>
          <Select
            value={provider}
            onValueChange={(value) => setProvider(value as PmsType)}
            disabled={!canWrite}
          >
            <SelectTrigger id="provider">
              <SelectValue
                placeholder={t("pms.credentials.providerPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {Object.values(PmsType).map((option) => (
                <SelectItem key={option} value={option}>
                  {t(PMS_PROVIDER_LABEL_KEYS[option])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showSwitchWarning && (
            <p className="text-destructive text-sm">
              {t("pms.credentials.providerSwitchWarning")}
            </p>
          )}
        </div>

        {provider === PmsType.OPERA ? (
          <OperaCredentialsForm
            settings={settings}
            credentials={credentials}
            canWrite={canWrite}
            isSaving={isSaving}
            onSave={onSave}
            missingFields={operaMissingFields}
          />
        ) : (
          <MewsCredentialsForm
            settings={settings}
            canWrite={canWrite}
            isSaving={isSaving}
            onSave={onSave}
          />
        )}
      </CardContent>
    </Card>
  );
};
